import { useGetContractedCompanies } from "@/entities/get-contracted-companies/fetch";
import type { CompanyListPageProps } from "@/pages/company-list/model";
import type { CompanyListSearchFormSchemaType } from "@/pages/company-list/model/schema";
import { useMemo, useState } from "react";

export const useCompanyListPageProps = (): CompanyListPageProps => {
  const [page, setPage] = useState(1);

  const [perPage, setPerPage] = useState(50);

  const [searchParams, setSearchParams] =
    useState<CompanyListSearchFormSchemaType>({
      companyName: "",
      address: "",
      companyCategories: [],
    });

  const getCompaniesResponse = useGetContractedCompanies({
    pagination: {
      pageNumber: page,
      displayLimit: perPage,
    },
    search: {
      name: searchParams.companyName,
      address: searchParams.address,
      category: searchParams.companyCategories,
    },
  });

  const companies: CompanyListPageProps["companies"] = (() => {
    if (getCompaniesResponse.isError) {
      return {
        status: "error",
        error: {
          message: JSON.stringify(getCompaniesResponse.error),
        },
      };
    }
    if (getCompaniesResponse.isLoading || getCompaniesResponse.isPending) {
      return {
        status: "loading",
        data: undefined,
      };
    }

    return {
      status: "success",
      data: {
        companies: getCompaniesResponse.data.data.companies.map((company) => ({
          ...company,
          // 型データの補正のために初期値を設定
          // 実際は必ず id は入っているはず
          id: company.id ?? 0,
        })),
        totalCount: getCompaniesResponse.data.data.totalCount,
      },
    };
  })();

  const pagination = useMemo(
    () =>
      getCompaniesResponse.data
        ? {
            page,
            perPage,
            totalCount: getCompaniesResponse.data.data.totalCount,
            onPageChange: setPage,
            onPerPageChange: setPerPage,
          }
        : undefined,
    [page, perPage, getCompaniesResponse.data],
  );

  return {
    companies,
    pagination,
    onSearch: setSearchParams,
  };
};
